import React, { useEffect, useState } from 'react';

const LS_KEY = 'idoc-labels-enabled';
export const IDOC_LABELS_EVENT = 'idoc-labels-changed';

export const IDocInspector: React.FC = () => {
  const [labelsEnabled, setLabelsEnabled] = useState(() => localStorage.getItem(LS_KEY) === 'true');
  const [inspectEnabled, setInspectEnabled] = useState(true);

  useEffect(() => {
    const handler = () => setLabelsEnabled(localStorage.getItem(LS_KEY) === 'true');
    window.addEventListener(IDOC_LABELS_EVENT, handler);
    return () => window.removeEventListener(IDOC_LABELS_EVENT, handler);
  }, []);

  useEffect(() => {
    if (!labelsEnabled) return;

    const relocateModalLabels = () => {
      document.querySelectorAll('[data-i-doc^="modal:"]').forEach(el => {
        const paper = el.querySelector('.MuiDialog-paper') as HTMLElement | null;
        if (paper && !paper.hasAttribute('data-i-doc')) {
          paper.setAttribute('data-i-doc', el.getAttribute('data-i-doc')!);
          el.removeAttribute('data-i-doc');
        }
      });
    };

    const observer = new MutationObserver(relocateModalLabels);
    observer.observe(document.body, { childList: true, subtree: true });
    relocateModalLabels();

    return () => observer.disconnect();
  }, [labelsEnabled]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'I') {
        setInspectEnabled(prev => !prev);
      }
    };

    const handleClick = (e: MouseEvent) => {
      if (!labelsEnabled || !inspectEnabled) return;
      const target = e.target as HTMLElement;
      const rect = target.getBoundingClientRect();
      if (
        e.clientX >= rect.left && e.clientX <= rect.left + 100 &&
        e.clientY >= rect.top && e.clientY <= rect.top + 16
      ) {
        const parent = target.closest('[data-i-doc]');
        const idoc = parent?.getAttribute('data-i-doc');
        if (idoc) {
          navigator.clipboard.writeText(idoc);
          console.log(`[IDocInspector] Copied: ${idoc}`);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('click', handleClick, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('click', handleClick, true);
    };
  }, [labelsEnabled, inspectEnabled]);

  if (!labelsEnabled || !inspectEnabled) return null;

  return (
    <style>{`
      [data-i-doc^="page:"] {
        outline: 2px dashed rgba(25, 118, 210, 0.18) !important;
        transition: outline-color 0.15s !important;
      }
      [data-i-doc^="page:"]::after  { background: rgba(25, 118, 210, 0.28) !important; }
      [data-i-doc^="page:"]:hover   { outline-color: rgba(25, 118, 210, 0.8) !important; }
      [data-i-doc^="page:"]:hover::after { background: rgba(25, 118, 210, 0.88) !important; }

      [data-i-doc^="tab:"] {
        outline: 2px dashed rgba(56, 142, 60, 0.18) !important;
        transition: outline-color 0.15s !important;
      }
      [data-i-doc^="tab:"]::after   { background: rgba(56, 142, 60, 0.28) !important; }
      [data-i-doc^="tab:"]:hover    { outline-color: rgba(56, 142, 60, 0.8) !important; }
      [data-i-doc^="tab:"]:hover::after  { background: rgba(56, 142, 60, 0.88) !important; }

      [data-i-doc^="modal:"] {
        outline: 2px dashed rgba(245, 0, 87, 0.18) !important;
        transition: outline-color 0.15s !important;
      }
      [data-i-doc^="modal:"]::after  { background: rgba(245, 0, 87, 0.28) !important; }
      [data-i-doc^="modal:"]:hover   { outline-color: rgba(245, 0, 87, 0.8) !important; }
      [data-i-doc^="modal:"]:hover::after { background: rgba(245, 0, 87, 0.88) !important; }

      [data-i-doc^="sec:"] {
        outline: 2px dashed rgba(230, 119, 0, 0.18) !important;
        transition: outline-color 0.15s !important;
      }
      [data-i-doc^="sec:"]::after    { background: rgba(230, 119, 0, 0.28) !important; }
      [data-i-doc^="sec:"]:hover     { outline-color: rgba(230, 119, 0, 0.8) !important; }
      [data-i-doc^="sec:"]:hover::after   { background: rgba(230, 119, 0, 0.88) !important; }

      [data-i-doc^="page:"],
      [data-i-doc^="tab:"],
      [data-i-doc^="sec:"],
      [data-i-doc^="modal:"] {
        position: relative !important;
      }
      [data-i-doc]::after {
        content: attr(data-i-doc);
        position: absolute;
        top: 0;
        left: 0;
        color: white;
        font-size: 10px;
        padding: 2px 4px;
        z-index: 999;
        cursor: pointer !important;
        border-radius: 0 0 4px 0;
        font-family: monospace;
        white-space: nowrap;
        transition: background 0.15s !important;
      }
    `}</style>
  );
};
